import { useCallback, useEffect, useRef, useState } from 'react'

import { useMutation } from '@apollo/client/react'
import { Temporal } from '@js-temporal/polyfill'

import { graphql } from '~publish/graphql'

import type {
  ChannelMapping,
  ImportItem,
  ImportItemMedia,
  ImportItemStatus,
} from '../MigrationWizard/types'

const CreatePostMutation = graphql(`
  mutation MigrationCreatePost($input: CreatePostInput!) {
    createPost(input: $input) {
      ... on PostActionSuccess {
        post {
          id
        }
      }
      ... on LimitReachedError {
        message
      }
      ... on InvalidInputError {
        message
      }
      ... on NotFoundError {
        message
      }
      ... on UnexpectedError {
        message
      }
      ... on RestProxyError {
        message
      }
    }
  }
`)

function buildAssetsInput(media: ImportItemMedia): Array<{
  image: { url: string; metadata: { altText: string } }
}> {
  return [
    {
      image: {
        url: media.url,
        metadata: { altText: media.altText || '' },
      },
    },
  ]
}

type ImportProgress = {
  total: number
  processed: number
  succeeded: number
  failed: number
  isRunning: boolean
  isComplete: boolean
  errors: Array<{ sourceRow: number; message: string }>
}

type UseImportExecutionArgs = {
  items: ImportItem[]
  channelMappings: ChannelMapping[]
  timezone: string
  concurrency?: number
  onSaveProgress: (statuses: Record<string, ImportItemStatus>) => void
}

const DEFAULT_CONCURRENCY = 3
const UI_UPDATE_INTERVAL_MS = 500

export function useImportExecution({
  items,
  channelMappings,
  timezone,
  concurrency = DEFAULT_CONCURRENCY,
  onSaveProgress,
}: UseImportExecutionArgs): ImportProgress & {
  start: () => void
  abort: () => void
} {
  const [progress, setProgress] = useState<ImportProgress>({
    total: 0,
    processed: 0,
    succeeded: 0,
    failed: 0,
    isRunning: false,
    isComplete: false,
    errors: [],
  })

  const abortRef = useRef(false)
  const startedRef = useRef(false)
  const onSaveProgressRef = useRef(onSaveProgress)
  onSaveProgressRef.current = onSaveProgress

  const [createPost] = useMutation(CreatePostMutation, {
    fetchPolicy: 'no-cache',
  })

  const getImportableItems = useCallback((): Array<
    ImportItem & { mappedChannelId: string }
  > => {
    const mappingLookup = new Map<string, string>()
    for (const mapping of channelMappings) {
      if (mapping.mappedChannelId) {
        mappingLookup.set(mapping.sourceKey, mapping.mappedChannelId)
      }
    }

    return items.flatMap((item) => {
      if (item.status !== 'valid' && item.status !== 'success') return []

      const key = `${item.channel.sourcePlatform}|${item.channel.sourceAccountName}`
      const mappedChannelId = mappingLookup.get(key)
      if (!mappedChannelId) return []

      return [{ ...item, mappedChannelId }]
    })
  }, [items, channelMappings])

  const start = useCallback(async () => {
    if (startedRef.current) return
    startedRef.current = true
    abortRef.current = false

    const importableItems = getImportableItems()

    // Count already-completed items from a resumed session
    const alreadySucceeded = importableItems.filter(
      (i) => i.status === 'success',
    ).length
    const pendingItems = importableItems.filter((i) => i.status !== 'success')

    // Mutable counters — shared across concurrent workers
    let succeeded = alreadySucceeded
    let failed = 0
    let processed = alreadySucceeded
    const errors: Array<{ sourceRow: number; message: string }> = []

    // Status map — flushed to the full session periodically via onSaveProgress
    const statuses: Record<string, ImportItemStatus> = {}
    for (const item of importableItems) {
      if (item.status === 'success') {
        statuses[item.id] = 'success'
      }
    }
    let progressDirty = false

    const nowMs = Date.now()

    setProgress({
      total: importableItems.length,
      processed: alreadySucceeded,
      succeeded: alreadySucceeded,
      failed: 0,
      isRunning: true,
      isComplete: false,
      errors: [],
    })

    // Batched UI updates — flush on interval rather than per item
    let uiDirty = false

    const flushUI = (): void => {
      setProgress({
        total: importableItems.length,
        processed,
        succeeded,
        failed,
        isRunning: true,
        isComplete: false,
        errors: [...errors],
      })
      uiDirty = false
    }

    const uiTimer = setInterval(() => {
      if (uiDirty) flushUI()
      if (progressDirty) {
        onSaveProgressRef.current({ ...statuses })
        progressDirty = false
      }
    }, UI_UPDATE_INTERVAL_MS)

    // Process a single item
    const processItem = async (
      item: (typeof pendingItems)[0],
    ): Promise<void> => {
      const shouldSaveAsDraft =
        item.post.originalStatus === 'sent' ||
        item.post.originalStatus === 'unknown' ||
        item.post.originalStatus === 'draft'

      const dueAt = item.post.scheduledTime
        ? adjustForTimezone(item.post.scheduledTime, timezone)
        : undefined

      const isFutureDate = dueAt ? new Date(dueAt).getTime() > nowMs : false
      const saveToDraft = shouldSaveAsDraft || !isFutureDate

      const assets =
        item.post.media?.type === 'image'
          ? buildAssetsInput(item.post.media)
          : undefined

      let itemStatus: ImportItemStatus = 'success'

      try {
        const { data } = await createPost({
          variables: {
            input: {
              channelId: item.mappedChannelId,
              text: item.post.text || '',
              schedulingType: 'automatic',
              mode: saveToDraft ? 'addToQueue' : 'customScheduled',
              saveToDraft,
              dueAt: !saveToDraft ? dueAt : undefined,
              source: 'migration_import',
              assets,
            },
          },
        })

        const result = data?.createPost
        if (result && '__typename' in result) {
          if (result.__typename === 'PostActionSuccess') {
            succeeded++
          } else {
            failed++
            itemStatus = 'failed'
            const errorMessage =
              'message' in result ? result.message : 'Unknown error'
            errors.push({ sourceRow: item.sourceRow, message: errorMessage })
          }
        } else {
          succeeded++
        }
      } catch (err) {
        failed++
        itemStatus = 'failed'
        errors.push({
          sourceRow: item.sourceRow,
          message: err instanceof Error ? err.message : 'Network error',
        })
      }

      processed++
      uiDirty = true

      statuses[item.id] = itemStatus
      progressDirty = true
    }

    // Run with concurrency
    let cursor = 0
    const processNextItem = async (): Promise<void> => {
      if (cursor >= pendingItems.length || abortRef.current) return

      const item = pendingItems[cursor]
      cursor += 1
      await processItem(item)
      await processNextItem()
    }
    const workers = Array.from(
      { length: Math.min(concurrency, pendingItems.length) },
      processNextItem,
    )
    await Promise.all(workers)

    // Cleanup — flush final progress to session
    clearInterval(uiTimer)
    onSaveProgressRef.current({ ...statuses })

    setProgress({
      total: importableItems.length,
      processed,
      succeeded,
      failed,
      isRunning: false,
      isComplete: true,
      errors: [...errors],
    })
  }, [createPost, getImportableItems, timezone, concurrency])

  // Auto-start on mount; abort workers on unmount so a
  // "Continue later" dismissal actually pauses the import.
  useEffect(() => {
    start()
    return (): void => {
      abortRef.current = true
    }
  }, [start])

  const abort = useCallback(() => {
    abortRef.current = true
  }, [])

  return { ...progress, start, abort }
}

/**
 * Interpret a Date (parsed as wall-clock time from the CSV) in the
 * user's selected timezone and return an ISO instant string.
 *
 * Uses Temporal to correctly handle DST transitions.
 */
export function adjustForTimezone(date: Date, timezone: string): string {
  const plain = Temporal.PlainDateTime.from({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
  })
  const zoned = plain.toZonedDateTime(timezone)
  return zoned.toInstant().toString()
}
